import { useState, useRef, useEffect } from 'react'
import { X, ChevronDown } from 'lucide-react'
import { useTags } from '../hooks/use-tags'
import type { Tag } from '@repo/shared'

interface TagSelectorProps {
  selectedIds: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
}

export function TagSelector({
  selectedIds,
  onChange,
  placeholder = 'Selecionar tags...',
}: TagSelectorProps) {
  const { data: tags = [] } = useTags()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedTags = tags.filter((t) => selectedIds.includes(t.id))
  const filteredTags = tags.filter(
    (t) =>
      !selectedIds.includes(t.id) &&
      t.name.toLowerCase().includes(search.toLowerCase()),
  )

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelect = (tag: Tag) => {
    onChange([...selectedIds, tag.id])
    setSearch('')
  }

  const handleRemove = (tagId: string) => {
    onChange(selectedIds.filter((id) => id !== tagId))
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Input area */}
      <div
        onClick={() => setIsOpen(true)}
        className="min-h-[38px] flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white cursor-text hover:border-gray-300 transition-colors"
      >
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
            style={{
              backgroundColor: tag.color ? `${tag.color}20` : '#f3f4f6',
              color: tag.color ?? '#6b7280',
            }}
          >
            {tag.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleRemove(tag.id)
              }}
              className="hover:opacity-70"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {isOpen ? (
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={selectedTags.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[80px] text-sm outline-none bg-transparent"
            autoFocus
          />
        ) : (
          selectedTags.length === 0 && (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )
        )}
        <ChevronDown className="h-4 w-4 text-gray-400 ml-auto flex-shrink-0" />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredTags.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">
              {search ? 'Nenhuma tag encontrada' : 'Todas as tags selecionadas'}
            </div>
          ) : (
            filteredTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleSelect(tag)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color ?? '#9ca3af' }}
                />
                {tag.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
